import React, { useContext, useEffect, useState } from 'react';
import { CustomWidgetContext } from '@/features/editor/widgets/custom-widget/shared/context';
import { DeviceType } from '@/utils/definitions';
import {
  Product,
  SlotState,
} from '@/features/editor/widgets/custom-widget/loading-section/shared/models';
import { mutateFn, useStateWithHistory } from '@/utils/use-complex-state';
import {
  CatalogWidgetProps,
  RecommendationOptions,
  RecommendationType,
} from '@/webapi/use-widget-catalog-api';
import { QBItemSelection } from '@/components/query-builder/models';
import { ExternalStrategyType } from '@/features/strategies/types';
import { defaultCondition } from '@/features/editor/widgets/custom-widget/loading-section/util/defaultCondition';
import { getEmptySlots } from '@/features/editor/widgets/custom-widget/loading-section/shared/util/getEmptySlots';
import { hashSlot } from '@/features/editor/widgets/custom-widget/loading-section/shared/util/hashSlot';
import { searchCtx } from '@/features/editor/widgets/custom-widget/loading-section/shared/search-ctx';
import { newUndoRedoContext } from '@/features/editor/widgets/custom-widget/shared/undo-redo-counter-context';
import { useSlotsStrategyApi } from '@/features/editor/widgets/custom-widget/loading-section/strategy-per-slot/saved-strategy/use-slots-strategy-api';
import { SavedStrategies } from '@/features/editor/widgets/custom-widget/loading-section/strategy-per-slot/saved-strategy/saved-filters';

export interface StrategyPerSlotCtx {
  state: State;
  selectProductForSlot: (p: Product, num: number) => void;
  changeNumForSlot: (num: number, newNum: number) => void;
  setStrategy: (slots: Array<SlotState>) => void;
  saveStrategy: (label: string) => void;
  updateStrategy: (id: string) => void;
  deleteStrategy: (id: string) => void;
  slotsStrategies: SavedStrategies;
  clearSlot: (num: number) => void;

  productCondSlotOpen: number | undefined;
  setProductCondSlotOpen: (x: number | undefined) => any;
  isProductCondOpen: boolean;
  setSlotCond: (cond: Array<QBItemSelection>) => void;
  getCurrentCond: () => Array<QBItemSelection>;
  selectStrategyForSlot: (s: RecommendationType, num: number) => void;
  duplicateSlot: (duplicatedNum: number, num: number) => void;
  getOpenCondSlot: () => SlotState;

  manualImageSelectionOpen: Product | undefined;
  setManualImageSelectionOpen: (x: Product | undefined) => void;
  isManualImageSelectionOpen: boolean;

  productSearchSlotOpen: number;
  setProductSearchSlotOpen: (n: number) => void;
  isProductSearchOpen: boolean;

  searchText: string;
  setSearchText: (x: string) => void;
  searchLoading: boolean;
  searchResult: Product[];
  searchTotal: number;
  searchLoadMore: () => void;
  searchHasMore: boolean;
  isPostPurchase: boolean;
  updateRecommendationOptions?: (options: RecommendationOptions) => void;

  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undoRedoContext: { undoRedoCount: number };
  isExternalStrategy?: boolean;
  externalStrategyType?: ExternalStrategyType;
  externalResources?: {
    collections: Array<any>;
    products: Array<any>;
    tags: string[];
  };

  globalFilter?: Array<QBItemSelection>;
  setGlobalFilter?: (cond: Array<QBItemSelection>) => void;
  setIsGlobalFilterOpen?: (b: boolean) => void;
  isGlobalFilterOpen?: boolean;
  saveGlobalFilter?: () => void;
  deleteGlobalFilter?: () => void;
  isGlobalFilterApplied?: boolean;
  hideSlotsStrategies?: boolean;
}

export const StrategyPerSlotContext = React.createContext(
  {} as StrategyPerSlotCtx,
);

type State = {
  slots: Array<SlotState>;
};

export function newStrategyPerSlotCtx(
  device: DeviceType,
  isPostPurchase: boolean,
  initial?: Array<SlotState>,
) {
  const { currentSchema, currentWidget } = useContext(CustomWidgetContext);
  const initialState = initial || getInitialState(device);

  const { getListSlotsStrategies, saveSlotsStrategy, deleteSlotsStrategy } =
    useSlotsStrategyApi();
  const [slotsStrategies, setSlotsStrategy] = useState<SavedStrategies>([]);

  useEffect(() => {
    const fetchSavedStrategies = async () => {
      const strategies = await getListSlotsStrategies();
      if (strategies && strategies.length > 0) {
        setSlotsStrategy(strategies);
      }
    };

    fetchSavedStrategies();
  }, []);
  const [stableInstanceId] = useState(
    () =>
      `instance_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
  );

  const {
    redo,
    undo,
    canRedo,
    canUndo,
    produceDispatch: changeState,
    state,
    undoRedoCount,
  } = useStateWithHistory<{
    slots: Array<SlotState>;
  }>(
    { slots: initialState },
    `STRATEGY_PER_SLOT_${currentWidget?.id || stableInstanceId}_HISTORY`,
  );
  useWatchSlotsSize(changeState, currentSchema, device);

  const undoRedoContext = newUndoRedoContext(undoRedoCount);

  const {
    globalFilter,
    setGlobalFilter,
    setIsGlobalFilterOpen,
    isGlobalFilterOpen,
    saveGlobalFilter,
    deleteGlobalFilter,
    isGlobalFilterApplied,
    setIsGlobalFilterApplied,
  } = globalCondition(changeState, state, currentSchema?.appId);

  const selectProductForSlot = (p: Product, num: number) => {
    changeState((draft) => {
      draft.slots.forEach((slot) => {
        if (slot.num === num) {
          slot.manualProduct = p;
          delete slot.strategy;
          if (!slot.filter) {
            slot.filter = isGlobalFilterApplied
              ? globalFilter
              : defaultCondition(undefined, currentSchema?.appId);
            slot.hash = hashSlot(slot);
          }
        }
      });
    });
    setProductSearchSlotOpen(undefined);
  };

  const selectStrategyForSlot = (s: RecommendationType, num: number) => {
    changeState((draft) => {
      draft.slots.forEach((slot) => {
        if (slot.num === num) {
          slot.strategy = s;
          delete slot.manualProduct;
          if (!slot.filter) {
            slot.filter = isGlobalFilterApplied
              ? globalFilter
              : defaultCondition(s, currentSchema?.appId);
            slot.hash = hashSlot(slot);
          }
        }
      });
    });
  };
  const duplicateSlot = (duplicatedNum: number, slotNum: number) => {
    changeState((draft) => {
      const duplicatedSlot = draft.slots[duplicatedNum];
      if (duplicatedSlot) {
        draft.slots.forEach((slot) => {
          if (slot.num === slotNum) {
            slot.strategy = duplicatedSlot.strategy;
            slot.manualProduct = duplicatedSlot.manualProduct;
            slot.filter = duplicatedSlot.filter;
            slot.hash = hashSlot(slot);
          }
        });
      }
    });
  };

  const changeNumForSlot = (num: number, newNum: number) => {
    changeState((draft) => {
      draft.slots.forEach((slot) => {
        if (slot.num === num) {
          slot.num = newNum;
        } else if (slot.num === newNum) {
          slot.num = num;
        }
      });
      draft.slots.sort((a, b) => a.num - b.num);
    });
  };

  const setStrategy = (slots: Array<SlotState>) => {
    const defaultFilter = defaultCondition(RecommendationType.MANUAL);
    setIsGlobalFilterApplied(false);
    setGlobalFilter(defaultFilter);
    let newGlobalFilterIsApplied = false;
    changeState((draft) => {
      draft.slots.forEach((slot) => {
        delete slot.manualProduct;
        delete slot.strategy;
        delete slot.filter;
        delete slot.isGlobalFilter;
        delete slot.hash;

        const newSlot = slots.find((s) => s.num === slot.num);
        if (newSlot) {
          Object.assign(slot, newSlot);
          if (!newSlot.filter) {
            slot.filter = defaultFilter;
          }
          if (
            !newGlobalFilterIsApplied &&
            newSlot.isGlobalFilter &&
            newSlot.filter
          ) {
            setIsGlobalFilterApplied(true);
            setGlobalFilter(newSlot.filter);
            newGlobalFilterIsApplied = true;
          }
        }
      });
      draft.slots.sort((a, b) => a.num - b.num);
    });
  };

  const saveStrategy = async (name: string) => {
    const strategy = {
      name,
      slots: { slots: state.slots },
    };

    await saveSlotsStrategy(strategy);
    const strategies = await getListSlotsStrategies();
    setSlotsStrategy(strategies);
  };

  const updateStrategy = async (id: string) => {
    const strategy = {
      id,
      slots: { slots: state.slots },
    };

    await saveSlotsStrategy(strategy);
    const strategies = await getListSlotsStrategies();
    setSlotsStrategy(strategies);
  };

  const deleteStrategy = async (id: string) => {
    await deleteSlotsStrategy(id);
    const strategies = await getListSlotsStrategies();
    setSlotsStrategy(strategies);
  };

  const clearSlot = (num: number) => {
    changeState((draft) => {
      draft.slots.forEach((slot) => {
        if (slot.num === num) {
          delete slot.manualProduct;
          delete slot.strategy;
          if (!isGlobalFilterApplied) delete slot.filter;
          delete slot.hash;
        }
      });
    });
  };

  const {
    productCondSlotOpen,
    setProductCondSlotOpen,
    isProductCondOpen,
    setSlotCond,
    getCurrentCond,
    getOpenCondSlot,
  } = slotCondition(changeState, state, globalFilter);

  const {
    productSearchSlotOpen,
    setProductSearchSlotOpen,
    isProductSearchOpen,
    searchState,
    setSearchText,
    loading: searchLoading,
    loadMore,
    manualImageSelectionOpen,
    setManualImageSelectionOpen,
    isManualImageSelectionOpen,
  } = searchCtx();

  return {
    manualImageSelectionOpen,
    setManualImageSelectionOpen,
    isManualImageSelectionOpen,
    getOpenCondSlot,
    selectStrategyForSlot,
    changeNumForSlot,
    setStrategy,
    saveStrategy,
    updateStrategy,
    deleteStrategy,
    slotsStrategies,
    getCurrentCond,
    setSlotCond,
    isProductCondOpen,
    productCondSlotOpen,
    setProductCondSlotOpen,
    selectProductForSlot,
    duplicateSlot,
    state,
    clearSlot,
    productSearchSlotOpen,
    setProductSearchSlotOpen,
    isProductSearchOpen,

    setSearchText,
    searchLoading,
    searchText: searchState?.searchText,
    searchResult: searchState.products,
    searchTotal: searchState.products.length,
    searchLoadMore: loadMore,
    searchHasMore: searchState.hasMore,
    isPostPurchase,

    redo,
    undo,
    canRedo,
    canUndo,
    undoRedoContext,

    globalFilter,
    setGlobalFilter,
    setIsGlobalFilterOpen,
    isGlobalFilterOpen,
    saveGlobalFilter,
    deleteGlobalFilter,
    isGlobalFilterApplied,
    hideSlotsStrategies: false,
  } as StrategyPerSlotCtx;
}

function getInitialState(device: DeviceType) {
  const { currentSchema } = useContext(CustomWidgetContext);
  const savedState = currentSchema.settings?.loading?.loadingEnv
    ?.recommendationOptions?.strategyPerSlot as Array<SlotState>;
  return getEmptySlots(currentSchema, device).map(
    (s) => savedState?.find((x) => x.num === s.num) || s,
  );
}
function useWatchSlotsSize(
  changeState: (fn: mutateFn<State>) => void,
  currentSchema: CatalogWidgetProps,
  device: DeviceType,
) {
  useEffect(() => {
    changeState((draft) => {
      if (getEmptySlots(currentSchema, device).length !== draft.slots.length)
        draft.slots = getEmptySlots(currentSchema, device).map(
          (empty) =>
            draft.slots.find((existing) => existing.num === empty.num) || empty,
        );
    });
  }, [getEmptySlots(currentSchema, device).length]);
}
function slotCondition(
  changeState: (fn: mutateFn<State>) => void,
  state: State,
  globalFilter?: Array<QBItemSelection>,
) {
  const [productCondSlotOpen, setProductCondSlotOpen] = useState<
    number | undefined
  >();
  const isProductCondOpen = typeof productCondSlotOpen !== `undefined`;
  const setSlotCond = (cond: Array<QBItemSelection>) => {
    changeState((draft) => {
      draft.slots.forEach((slot) => {
        if (slot.num === productCondSlotOpen) {
          slot.filter = cond;
          if (
            slot.isGlobalFilter === true &&
            globalFilter &&
            cond !== globalFilter
          ) {
            delete slot.isGlobalFilter;
          }
          slot.hash = hashSlot(slot);
        }
      });
    });
  };
  const getCurrentCond = () =>
    state.slots.find((s) => s.num === productCondSlotOpen)?.filter;

  const getOpenCondSlot = () =>
    state.slots.find((s) => s.num === productCondSlotOpen);
  return {
    productCondSlotOpen,
    setProductCondSlotOpen,
    isProductCondOpen,
    setSlotCond,
    getCurrentCond,
    getOpenCondSlot,
  };
}

function globalCondition(
  changeState: (fn: mutateFn<State>) => void,
  state: State,
  id?: string,
) {
  const defaultCond = defaultCondition(RecommendationType.MANUAL, id);
  const [isGlobalFilterOpen, setIsGlobalFilterOpen] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState(defaultCond);
  const [isGlobalFilterApplied, setIsGlobalFilterApplied] =
    useState<boolean>(false);

  useEffect(() => {
    const globalFilterSlot = state.slots.find(
      (slot) => slot.isGlobalFilter === true,
    );
    if (globalFilterSlot) {
      setGlobalFilter(globalFilterSlot.filter);
      setIsGlobalFilterApplied(true);
    } else {
      setGlobalFilter(defaultCond);
      setIsGlobalFilterApplied(false);
    }
  }, [state.slots]);

  const saveGlobalFilter = () => {
    if (globalFilter) {
      changeState((draft) => {
        draft.slots.forEach((slot) => {
          slot.filter = globalFilter;
          slot.isGlobalFilter = true;
          if (!slot.hash) slot.hash = hashSlot(slot);
        });
      });
    }
  };

  const deleteGlobalFilter = () => {
    if (globalFilter) {
      changeState((draft) => {
        draft.slots.forEach((slot) => {
          if (slot.isGlobalFilter === true) {
            slot.filter = defaultCond;
            delete slot.isGlobalFilter;
          }
        });
      });
    }
  };

  return {
    globalFilter,
    setGlobalFilter,
    setIsGlobalFilterOpen,
    isGlobalFilterOpen,
    saveGlobalFilter,
    deleteGlobalFilter,
    isGlobalFilterApplied,
    setIsGlobalFilterApplied,
  };
}
